/**
 * Page-level reducer for the Test Suite Configuration form.
 *
 * Manages all form state via `useReducer` with `ConfigPageState` and
 * `ConfigPageAction`. Provides predictable state transitions and makes
 * it straightforward to reset state when a different tool is selected.
 *
 * @see types.ts for ConfigPageState and ConfigPageAction definitions
 */

import {
  ConfigPageState,
  ConfigPageAction,
  PrePopulatedConfigResponse,
  SchemaDerivationResult,
  TemplateDefinition,
  GroupExpansionEntry,
} from './types';

// ─── Helpers: Stable key generation ────────────────────────────────────

let stableKeyCounter = 0;

/**
 * Generates a unique stable key for React reconciliation of template cards.
 * Uses a monotonically increasing counter combined with a timestamp prefix
 * to guarantee uniqueness across the session.
 */
function generateStableKey(): string {
  return `tpl_${Date.now()}_${++stableKeyCounter}`;
}

/**
 * Ensures every template in a tool-grouped record has a `_stableKey`.
 * Templates that already have one are left unchanged.
 */
function ensureStableKeys(
  templates: Record<string, Array<TemplateDefinition & { enabled: boolean }>>,
): Record<string, Array<TemplateDefinition & { enabled: boolean }>> {
  const result: Record<string, Array<TemplateDefinition & { enabled: boolean }>> = {};
  for (const [toolName, list] of Object.entries(templates)) {
    result[toolName] = list.map((t) =>
      t._stableKey ? t : { ...t, _stableKey: generateStableKey() },
    );
  }
  return result;
}

/**
 * Ensures every group expansion entry in a param-grouped record has a `_stableKey`.
 * Entries that already have one are left unchanged.
 */
function ensureGroupExpansionStableKeys(
  groupExpansion: Record<string, GroupExpansionEntry[]>,
): Record<string, GroupExpansionEntry[]> {
  const result: Record<string, GroupExpansionEntry[]> = {};
  for (const [paramName, entries] of Object.entries(groupExpansion)) {
    result[paramName] = entries.map((e) =>
      e._stableKey ? e : { ...e, _stableKey: generateStableKey() },
    );
  }
  return result;
}

// ─── Initial State ─────────────────────────────────────────────────────

export const initialConfigPageState: ConfigPageState = {
  // Tool Selection
  selectedToolId: null,
  selectedRevisionTrack: 'live',
  selectedRevision: null,
  schemaSummary: null,
  schemaContent: '',

  // User Inputs
  businessKnowledge: '',
  rootQuestions: '',

  // Pre-population state
  isPrePopulated: false,

  // LLM Configuration
  llm: {
    polisher: {
      model: 'us.anthropic.claude-opus-4-6-v1',
      temperature: 0.3,
      max_tokens: 2000,
      region_name: 'us-east-1',
    },
    validator: {
      enabled: false,
      model: 'us.anthropic.claude-sonnet-4-6',
      temperature: 0.1,
      max_tokens: 2000,
      region_name: 'us-east-1',
    },
  },

  // Tool Configuration
  tools: {},

  // Jargon Prompt
  jargonPrompt: '',

  // Templates (pattern library with enable/disable)
  templates: {},

  // Group Expansions
  groupExpansion: {},

  // Sampling & Selection
  metricsSelection: {
    coupled_with_other_params: true,
    enum: [],
  },
  dimensionsSelection: {
    enabled: true,
    probability: 0.4,
    min_values: 1,
    max_values: 2,
    enum: [],
  },
  parameterOutputFormat: {
    ignore_patterns: ['limit', 'orderBy'],
    criteria_params: {},
    apply_criteria_to_all_arrays: true,
  },
  schemaRequiredDefaults: {},
  paramNameMapping: {},
  llmPolish: {
    removable_values: [],
    must_preserve: [],
  },

  // Column Mapping
  columnMapping: {
    metrics: 'derived_metrics_available',
    min_date: 'min_date',
    max_date: 'max_date',
  },

  // Frequency Weight Columns
  frequencyWeightColumns: [],

  // OrderBy Selection
  orderBySelection: {
    enabled: true,
    probability: 0.2,
  },

  // Stratified Sampling
  stratifiedSampling: {
    enabled: false,
    frequency_weight_columns: [],
    stratify_columns: [],
    exclude_columns: [],
  },

  // Group Expansion top-level controls
  groupExpansionEnabled: true,
  groupExpansionProbability: 0.3,

  // Selection Settings
  selectionSettings: {
    questions_per_template: 10,
    diversity_enabled: true,
  },

  // Time Period Generation
  timePeriodGeneration: {
    enable_months: true,
    enable_quarters: true,
    enable_half_years: true,
    enable_years: true,
    enable_ytd: false,
    enable_qtd: false,
  },

  // Database Configuration
  databaseConfig: {
    type: 'redshift',
    cluster_identifier: 'fda_svls_cluster',
    database: 'fda_svls_db',
    stored_procedure: 'sp_filter_metrics_mapping',
    output_schema: 'bluey_output',
    source_table_name: '',
    base_metrics_catalog: '',
    derived_metrics_catalog: '',
    default_agg_columns: '["min(month_begin_date)", "max(month_begin_date)"]',
    max_workers: 4,
    secret_arn: {
      test: '',
      beta: '',
      live: '',
    },
    default_environment: 'test',
    aws_profile: '',
  },

  // Root Questions Config
  rootQuestionsConfig: {
    mode: 'v5_database_backed',
    v5_config: {},
  },

  // Output
  generatedYaml: '',
  suggestedFilename: '',
  s3TargetPath: '',
};

// ─── Helper: Reset config sections to initial defaults ─────────────────

/**
 * Returns a partial state object with all config sections reset to their
 * initial default values. Used by RESET_PREPOPULATION and when a
 * different tool is selected after pre-population.
 */
function getResetConfigSections(): Partial<ConfigPageState> {
  return {
    isPrePopulated: false,
    llm: {
      polisher: { ...initialConfigPageState.llm.polisher },
      validator: { ...initialConfigPageState.llm.validator },
    },
    tools: {},
    jargonPrompt: '',
    templates: {},
    groupExpansion: {},
    metricsSelection: {
      ...initialConfigPageState.metricsSelection,
      enum: [],
    },
    dimensionsSelection: {
      ...initialConfigPageState.dimensionsSelection,
      enum: [],
    },
    parameterOutputFormat: {
      ...initialConfigPageState.parameterOutputFormat,
      ignore_patterns: [...initialConfigPageState.parameterOutputFormat.ignore_patterns],
      criteria_params: {},
    },
    schemaRequiredDefaults: {},
    paramNameMapping: {},
    llmPolish: {
      removable_values: [],
      must_preserve: [],
    },
    columnMapping: { ...initialConfigPageState.columnMapping },
    frequencyWeightColumns: [],
    orderBySelection: {
      enabled: true,
      probability: 0.2,
    },
    stratifiedSampling: {
      enabled: false,
      frequency_weight_columns: [],
      stratify_columns: [],
      exclude_columns: [],
    },
    groupExpansionEnabled: true,
    groupExpansionProbability: 0.3,
    selectionSettings: { ...initialConfigPageState.selectionSettings },
    timePeriodGeneration: { ...initialConfigPageState.timePeriodGeneration },
    databaseConfig: {
      ...initialConfigPageState.databaseConfig,
      secret_arn: { ...initialConfigPageState.databaseConfig.secret_arn },
    },
    rootQuestionsConfig: {
      ...initialConfigPageState.rootQuestionsConfig,
      v5_config: {},
    },
    generatedYaml: '',
    suggestedFilename: '',
    s3TargetPath: '',
  };
}

// ─── Helper: Map pre-populated response to state ───────────────────────

/**
 * Maps all fields from a `PrePopulatedConfigResponse` into the
 * corresponding `ConfigPageState` fields.
 */
function applyPrePopulatedResponse(
  state: ConfigPageState,
  response: PrePopulatedConfigResponse,
): ConfigPageState {
  // The slimmed-down backend only returns the 3 fields that require LLM
  // intelligence: jargon_prompt, templates, and group_expansion.
  // All other fields (metrics, dimensions, param mapping, defaults, etc.)
  // are already populated by client-side schema derivation via
  // APPLY_SCHEMA_DERIVATION and should NOT be overwritten.

  // Backend templates don't include 'enabled' — default all to enabled: true
  const templatesWithEnabled: Record<string, Array<TemplateDefinition & { enabled: boolean }>> = {};
  for (const [toolName, list] of Object.entries(response.templates)) {
    templatesWithEnabled[toolName] = list.map((t) => ({
      ...t,
      enabled: t.enabled ?? true,
    }));
  }

  return {
    ...state,
    isPrePopulated: true,

    // LLM-generated content (only fields the backend returns)
    jargonPrompt: response.jargon_prompt,
    templates: ensureStableKeys(templatesWithEnabled),
    groupExpansion: ensureGroupExpansionStableKeys(response.group_expansion),
  };
}

// ─── Helper: Set a nested field via dot-path ───────────────────────────

/**
 * Sets a deeply nested field on an object using a dot-separated path.
 *
 * Example: `setNestedField(state, 'llm.polisher.temperature', 0.5)`
 * produces a new state with `state.llm.polisher.temperature === 0.5`,
 * with all intermediate objects shallow-cloned for immutability.
 */
function setNestedField<T extends Record<string, unknown>>(
  obj: T,
  path: string,
  value: unknown,
): T {
  const keys = path.split('.');
  if (keys.length === 0) return obj;

  if (keys.length === 1) {
    return { ...obj, [keys[0]]: value };
  }

  const [head, ...rest] = keys;
  const child = (obj[head] ?? {}) as Record<string, unknown>;
  return {
    ...obj,
    [head]: setNestedField(child, rest.join('.'), value),
  };
}

// ─── Helper: Duplicate a template ──────────────────────────────────────

function duplicateTemplate(
  template: TemplateDefinition & { enabled: boolean },
): TemplateDefinition & { enabled: boolean } {
  return {
    ...template,
    id: `${template.id}_copy_${Date.now()}`,
    _stableKey: generateStableKey(),
    parameters: {
      ...template.parameters,
      required: [...template.parameters.required],
      implicit: template.parameters.implicit
        ? { ...template.parameters.implicit }
        : undefined,
      comparison: template.parameters.comparison
        ? {
            ...template.parameters.comparison,
            placeholders: [...template.parameters.comparison.placeholders],
            fixed_params: template.parameters.comparison.fixed_params
              ? { ...template.parameters.comparison.fixed_params }
              : undefined,
          }
        : undefined,
    },
  };
}

// ─── Reducer ───────────────────────────────────────────────────────────

export function configPageReducer(
  state: ConfigPageState,
  action: ConfigPageAction,
): ConfigPageState {
  switch (action.type) {
    // ── Tool Selection ───────────────────────────────────────────────
    case 'SELECT_TOOL': {
      const toolChanged = state.selectedToolId !== action.toolId;
      const baseState: ConfigPageState = {
        ...state,
        selectedToolId: action.toolId,
        selectedRevision: null,
        schemaSummary: null,
        schemaContent: '',
      };

      // If the tool changed and we had pre-populated data, reset config
      if (toolChanged && state.isPrePopulated) {
        return {
          ...baseState,
          ...getResetConfigSections(),
        };
      }

      // If tool changed but not pre-populated, still clear schema-derived values
      if (toolChanged) {
        return {
          ...baseState,
          tools: {},
          templates: {},
          groupExpansion: {},
          metricsSelection: { ...initialConfigPageState.metricsSelection, enum: [] },
          dimensionsSelection: { ...initialConfigPageState.dimensionsSelection, enum: [] },
          schemaRequiredDefaults: {},
          paramNameMapping: {},
          llmPolish: { removable_values: [], must_preserve: [] },
          columnMapping: { ...initialConfigPageState.columnMapping },
          frequencyWeightColumns: [],
          orderBySelection: { ...initialConfigPageState.orderBySelection },
          stratifiedSampling: { ...initialConfigPageState.stratifiedSampling },
          groupExpansionEnabled: true,
          groupExpansionProbability: 0.3,
          generatedYaml: '',
          suggestedFilename: '',
          s3TargetPath: '',
        };
      }

      return baseState;
    }

    case 'SET_REVISION_TRACK':
      return {
        ...state,
        selectedRevisionTrack: action.track,
        // Clear stale revision data so the UI doesn't show the previous
        // track's confirmation while the new track's revision loads.
        selectedRevision: null,
        schemaSummary: null,
        schemaContent: '',
      };

    case 'SET_REVISION_DATA':
      return {
        ...state,
        selectedRevision: action.revision,
        schemaSummary: action.summary,
        schemaContent: action.schemaContent,
      };

    // ── User Inputs ──────────────────────────────────────────────────
    case 'SET_BUSINESS_KNOWLEDGE':
      return { ...state, businessKnowledge: action.value };

    case 'SET_ROOT_QUESTIONS':
      return { ...state, rootQuestions: action.value };

    // ── Pre-population ───────────────────────────────────────────────
    case 'POPULATE_FROM_RESPONSE':
      return applyPrePopulatedResponse(state, action.response);

    case 'RESET_PREPOPULATION':
      return {
        ...state,
        ...getResetConfigSections(),
      };

    // ── Schema Derivation ────────────────────────────────────────────
    case 'APPLY_SCHEMA_DERIVATION': {
      const { payload } = action;
      return {
        ...state,
        tools: payload.tools,
        templates: ensureStableKeys(payload.templates),
        metricsSelection: payload.metricsSelection,
        dimensionsSelection: payload.dimensionsSelection,
        schemaRequiredDefaults: payload.schemaRequiredDefaults,
        paramNameMapping: payload.paramNameMapping,
        columnMapping: payload.columnMapping,
        frequencyWeightColumns: payload.frequencyWeightColumns,
        groupExpansion: ensureGroupExpansionStableKeys(payload.groupExpansion),
        llmPolish: payload.llmPolish,
        stratifiedSampling: {
          ...state.stratifiedSampling,
          frequency_weight_columns: payload.frequencyWeightColumns,
          stratify_columns: payload.stratifyColumns,
        },
        // NOTE: isPrePopulated remains unchanged (false)
      };
    }

    // ── Generic field update (dot-path) ──────────────────────────────
    case 'UPDATE_FIELD':
      return setNestedField(
        state as unknown as Record<string, unknown>,
        action.path,
        action.value,
      ) as unknown as ConfigPageState;

    // ── Template actions ─────────────────────────────────────────────
    case 'TOGGLE_TEMPLATE': {
      const toolTemplates = state.templates[action.toolName];
      if (!toolTemplates) return state;

      return {
        ...state,
        templates: {
          ...state.templates,
          [action.toolName]: toolTemplates.map((t) =>
            t._stableKey === action.stableKey ? { ...t, enabled: !t.enabled } : t,
          ),
        },
      };
    }

    case 'UPDATE_TEMPLATE': {
      const toolTemplates = state.templates[action.toolName];
      if (!toolTemplates) return state;

      return {
        ...state,
        templates: {
          ...state.templates,
          [action.toolName]: toolTemplates.map((t) =>
            t._stableKey === action.stableKey
              ? { ...t, [action.field]: action.value }
              : t,
          ),
        },
      };
    }

    case 'ADD_TEMPLATE': {
      const existing = state.templates[action.toolName] ?? [];
      const newTemplate = action.template._stableKey
        ? action.template
        : { ...action.template, _stableKey: generateStableKey() };
      return {
        ...state,
        templates: {
          ...state.templates,
          [action.toolName]: [...existing, newTemplate],
        },
      };
    }

    case 'DUPLICATE_TEMPLATE': {
      const toolTemplates = state.templates[action.toolName];
      if (!toolTemplates) return state;

      const source = toolTemplates.find((t) => t._stableKey === action.stableKey);
      if (!source) return state;

      const copy = duplicateTemplate(source);
      return {
        ...state,
        templates: {
          ...state.templates,
          [action.toolName]: [...toolTemplates, copy],
        },
      };
    }

    case 'REMOVE_TEMPLATE': {
      const toolTemplates = state.templates[action.toolName];
      if (!toolTemplates) return state;

      return {
        ...state,
        templates: {
          ...state.templates,
          [action.toolName]: toolTemplates.filter(
            (t) => t._stableKey !== action.stableKey,
          ),
        },
      };
    }

    // ── Group expansion actions ──────────────────────────────────────
    case 'ADD_GROUP_EXPANSION': {
      const existing = state.groupExpansion[action.paramName] ?? [];
      const newEntry = action.entry._stableKey
        ? action.entry
        : { ...action.entry, _stableKey: generateStableKey() };
      return {
        ...state,
        groupExpansion: {
          ...state.groupExpansion,
          [action.paramName]: [...existing, newEntry],
        },
      };
    }

    case 'REMOVE_GROUP_EXPANSION': {
      const entries = state.groupExpansion[action.paramName];
      if (!entries) return state;

      return {
        ...state,
        groupExpansion: {
          ...state.groupExpansion,
          [action.paramName]: entries.filter((_, i) => i !== action.index),
        },
      };
    }

    case 'UPDATE_GROUP_EXPANSION': {
      const entries = state.groupExpansion[action.paramName];
      if (!entries || !entries[action.index]) return state;

      return {
        ...state,
        groupExpansion: {
          ...state.groupExpansion,
          [action.paramName]: entries.map((entry, i) =>
            i === action.index
              ? { ...entry, [action.field]: action.value }
              : entry,
          ),
        },
      };
    }

    // ── Output ───────────────────────────────────────────────────────
    case 'SET_GENERATED_YAML':
      return {
        ...state,
        generatedYaml: action.yaml,
        suggestedFilename: action.filename,
      };

    case 'SET_S3_TARGET_PATH':
      return {
        ...state,
        s3TargetPath: action.path,
      };

    default:
      return state;
  }
}
